import React, { useEffect, useMemo, useState } from "react";
import { Download as DownloadIcon } from "@mui/icons-material";
import { Alert, Box, Button, Divider, IconButton, Paper, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";

import { LoadingButton, Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import { useCreateExportMutation, useGetExportsQuery } from "fond/api/exportsSlice";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import { Export, ExportStatus, ISGLayerId } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

const MAX_COMPLETED_EXPORTS = 15;
interface LayerExportProps {
  layerId: ISGLayerId;
  displayName: string;
  title: string;
  hasData?: boolean;
}

const LayerExport: React.FC<LayerExportProps> = ({ layerId, displayName, title, hasData = false }) => {
  const { goBack } = useStackedNavigationContext();
  const project = useCurrentProject();
  const versionId = useAppSelector((state) => state.project.versionId);

  // Control whether we should poll for export status updates
  const [shouldPoll, setShouldPoll] = useState(true);

  // Fetch all exports for the project with polling
  // Poll while there are in-progress exports; otherwise, disable polling
  const { data: exportsData } = useGetExportsQuery(project?.ID ?? "", {
    skip: !project?.ID,
    pollingInterval: shouldPoll ? 2000 : 0, // Poll every 2 seconds while needed
  });

  // Export creation mutation
  const [createExport, { isLoading: isCreatingExport }] = useCreateExportMutation();

  // Filter exports to show only those for the current version and that include this layer
  const { completedExports, inProgressExports } = useMemo(() => {
    if (!exportsData?.entities || !versionId) {
      return { completedExports: [], inProgressExports: [] };
    }

    const filtered = Object.values(exportsData.entities).filter((exportItem): exportItem is Export => {
      if (!exportItem) return false;
      // Check if export is for the current version
      if (exportItem.VersionID !== versionId) return false;
      // Check if export includes this layer
      return exportItem.Layers.some((layer) => layer.LayerKey === layerId);
    });

    const completed = filtered
      .filter((exportItem) => exportItem.Status.Value === ExportStatus.Complete)
      .sort((a, b) => {
        // Sort by CompletedAt descending (newest first)
        if (!a.CompletedAt || !b.CompletedAt) return 0;
        return new Date(b.CompletedAt).getTime() - new Date(a.CompletedAt).getTime();
      })
      .slice(0, MAX_COMPLETED_EXPORTS); // Limit to latest exports

    const inProgress = filtered.filter(
      (exportItem) => exportItem.Status.Value === ExportStatus.Pending || exportItem.Status.Value === ExportStatus.InProgress
    );

    return {
      completedExports: completed,
      inProgressExports: inProgress,
    };
  }, [exportsData, versionId, layerId]);

  // Enable polling only while there are in-progress exports
  useEffect(() => {
    const hasInProgress = inProgressExports.length > 0;
    setShouldPoll(hasInProgress);
  }, [inProgressExports.length]);

  const handleCancel = () => {
    goBack();
  };

  const handleExport = async () => {
    if (!versionId || !project) return;

    const timestamp = dayjs().format("YYYY-MM-DD-HHmmss");
    const exportName = `Export-${displayName}-${timestamp}`;

    const params = {
      VersionID: versionId,
      Name: exportName,
      Format: "shp", // Default format
      Description: `Export of ${displayName} layer`,
      BOM: false,
      SystemOfMeasurement: project.SystemOfMeasurement,
      SpliceTables: false,
      Comments: false,
      FiberCableSplit: false,
      HubSplit: false,
      SpliceSplit: false,
      Layers: [layerId],
      ScheduleOfRates: false,
    };

    try {
      await createExport(params).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar("Failed to generate export. Please try again...", { variant: "error" });
    }
  };

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={handleCancel} />
      <Typography fontWeight="500">Layer exports</Typography>

      {!hasData && (
        <Alert severity="info" sx={{ my: 1 }}>
          No data available to export. Please upload or draw data first.
        </Alert>
      )}

      {hasData && (
        <>
          {completedExports.length === 0 && inProgressExports.length === 0 && (
            <Alert severity="info" sx={{ my: 1 }}>
              There are currently no exports for this layer. Click "Create New Export" to get started.
            </Alert>
          )}

          {inProgressExports.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {inProgressExports.map((exportItem) => (
                <Paper key={exportItem.ID} variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
                  <LinearProgressWithLabel
                    title="Creating export..."
                    subtext={exportItem.Status.Value === ExportStatus.Pending ? "Pending..." : "Processing..."}
                    variant="determinate"
                    value={(exportItem.Status.Progress || 0) * 100}
                  />
                </Paper>
              ))}
            </Box>
          )}

          {completedExports.length > 0 && (
            <Table size="small" variant="ag-grid-compact" containerProps={{ sx: { my: 2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedExports.map((exportItem) => (
                  <TableRow key={exportItem.ID}>
                    <TableCell
                      sx={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {exportItem.Name || `Export-${displayName}-${exportItem.Format}`}
                    </TableCell>
                    <TableCell>{exportItem.CompletedAt ? dayjs(exportItem.CompletedAt).fromNow() : "-"}</TableCell>
                    <TableCell align="right">
                      <a href={exportItem.Url} download aria-label="download">
                        <IconButton size="small">
                          <DownloadIcon color="primary" />
                        </IconButton>
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
              <Button color="primary" size="small" onClick={handleCancel}>
                Back
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleExport}
                sx={{ ml: 1, px: 2 }}
                data-testid="export-button"
                loading={isCreatingExport}
                disabled={inProgressExports.length > 0}
              >
                Create New Export
              </LoadingButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LayerExport;
